<template>
  <EditorPopover
    ref="popover"
    @cancel="cancelEditing">

    <TabbedBox>
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-cutout">
          <i class="far fa-border-style-alt"></i> Corner Cutout
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <TabPage :first="true" tabid="tab-cutout">
          <div class="row">
            <div class="col-6">
              <div class="row form-group" v-if="isRectangleShape">
                <label class="col-form-label col-6 font-weight-bold">Cut Size</label>
                <div class="col-6">
                  <FormInputText type="number" v-model="width" addon="mm" />
                </div>
              </div>
              <div class="row form-group" v-if="isRectangleShape">
                <label class="col-form-label col-6 font-weight-bold">Cut Depth</label>
                <div class="col-6">
                  <FormInputText type="number" v-model="height" addon="mm" />
                </div>
              </div>
               <div class="row form-group" v-if="isRectangleShape">
                <label class="col-form-label col-6 font-weight-bold">Measure To</label>
                <div class="col-6">
                  <EditorPopoverOptionButton :active="cutoutMeasureType == CutoutMeasurementTypes.center" icon="fa-border-center-v" title="Center" @click="cutoutMeasureType = CutoutMeasurementTypes.center"></EditorPopoverOptionButton>
                  <EditorPopoverOptionButton :active="cutoutMeasureType == CutoutMeasurementTypes.edge" icon="fa-brackets" title="Edge" @click="cutoutMeasureType = CutoutMeasurementTypes.edge"></EditorPopoverOptionButton>
                </div>
              </div>
              <div class="row form-group" v-if="isCircleShape">
                <label class="col-form-label col-6 font-weight-bold">Diameter</label>
                <div class="col-6">
                  <FormInputText type="number" v-model="diameter" addon="mm" />
                </div>
              </div>
              <div class="row form-group" v-if="isCustomShape">
                <label class="col-form-label col-6 font-weight-bold">Custom Shape</label>
              </div>
              <div class="row form-group" v-if="isCustomShape">
                <label class="col-form-label col-6 font-weight-bold">Flipped</label>
                <div class="col-6">
                  <FormSwitch booleanMode v-model="isFlipped" />
                </div>
              </div>
            </div>
            <div class="col-6">
              <div class="row form-group" v-if="isRectangleShape">
                <label class="col-form-label col-6 pr-0 font-weight-bold">Corner Radius</label>
                <div class="col-6">
                  <FormInputText type="number" v-model="cornerRadius" />
                </div>
              </div> 
              <div class="row form-group" v-if="isRectangleShape">
                <label class="col-form-label col-6 font-weight-bold">Extend</label>
                <div class="col-6">
                  <FormInputText type="number" v-model="extendWidth" addon="mm" />
                </div>
              </div>
              <div class="row form-group" v-if="isCircleShape">
                <label class="col-form-label col-6 font-weight-bold">Inset</label>
                <div class="col-6">
                  <FormInputText type="number" v-model="inset" addon="mm" />
                </div>
              </div>
              <div class="row form-group">
                <label class="col-form-label col-6 font-weight-bold">Polished</label>
                <div class="col-6">
                  <FormSwitch :booleanMode="true" v-model="polished" />
                </div>
              </div>
              <div class="row form-group" v-if="false">
                <label class="col-form-label col-6 font-weight-bold">Offset From Edge</label>
                <div class="col-6">
                  <FormInputText type="number" v-model="cornerOffset" addon="mm" />
                </div>
              </div>
            </div>
          </div>

          <EditorPopoverButtonList v-if="editingEnabled">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelEditing" />
            <EditorPopoverButton title="Add Cutout" type="update" icon="fa-plus-circle" @click="addCutout" />
          </EditorPopoverButtonList>
        </TabPage>
      </template>
    </TabbedBox>

  </EditorPopover>
</template>


<script>
import _ from 'lodash'
import EditorPopover from '@glass-designer/components/editorPopover'
import EditorPopoverMixin from '@glass-designer/mixins/editorPopover'

import EditorKeypad from '@glass-designer/components/editorKeypad'
import EditorTypes from '@glass-designer/types'
import MathsUtils from '@/utils/mathsUtils'

import EditorPopoverButtonList from '@glass-designer/components/popover/editorPopoverButtonList'
import EditorPopoverButton from '@glass-designer/components/popover/editorPopoverButton'
import EditorPopoverOptionButton from '@glass-designer/components/popover/editorPopoverOptionButton'

import { CutoutMeasurementTypes, CutoutMeasurementTypeLookup } from '@glass-designer/types'

export default {
  name: 'EditorCornerCutoutToolPopover',
  mixins: [EditorPopoverMixin],
  components: {
    EditorPopover,
    EditorKeypad,
    EditorPopoverButtonList,
    EditorPopoverButton,
    EditorPopoverOptionButton
  },
  props: {
    editingEnabled: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      actionButtons: [],
      title: '',
      toolInfoText: '',
      shapeType: null,
      cornerRadius: 0,
      radius: 0,
      diameter: 0,
      inset: 0,
      cornerOffset: 0,
      width: 0,
      height: 0,
      extendWidth: 0,
      cutoutMeasureType: CutoutMeasurementTypes.center,
      isFlipped: false,
      polished: false
    }
  },
  computed: {
    isRectangleShape() {
      return this.shapeType == EditorTypes.LibraryItemShapeTypes.rectangle
    },
    isCircleShape() {
      return this.shapeType == EditorTypes.LibraryItemShapeTypes.circle
    },
    isCustomShape() {
      return this.shapeType == EditorTypes.LibraryItemShapeTypes.custom
    },
  },
  watch: {
    inset: function() {
      this.debouncedUpdateTool()
    },
    cornerRadius: function() {
      this.debouncedUpdateTool()
    },
    diameter: function() {
      this.debouncedUpdateTool()
    },
    width: function() {
      this.debouncedUpdateTool()
    },
    extendWidth: function() {
      this.debouncedUpdateTool()
    },
    cutoutMeasureType: function() {
      this.debouncedUpdateTool()
    },
    height: function() {
      this.debouncedUpdateTool()
    },
    isFlipped: function() {
      this.debouncedUpdateTool()
    },
    polished: function() {
      this.debouncedUpdateTool()
    }
  },
  created() {
    this.CutoutMeasurementTypes = CutoutMeasurementTypes
    this.CutoutMeasurementTypeLookup = CutoutMeasurementTypeLookup
    this.debouncedUpdateTool = _.debounce(this._updateTool, 100)
  },
  methods: {
    setup(editor, options) {
      this.clearOptions()

      options = _.defaults(options, {
        actionButtons: [],
        title: ''
      })

      this.actionButtons = options.actionButtons
      this.title = options.title
      this.radius = MathsUtils.roundNumber(options.radius || 0)
      this.diameter = MathsUtils.roundNumber(this.radius * 2.0)
      this.cornerRadius = MathsUtils.roundNumber(options.cornerRadius || 0)
      this.inset = MathsUtils.roundNumber(options.inset || 0)
      this.cornerOffset = MathsUtils.roundNumber(options.cornerOffset || 0)
      this.width = MathsUtils.roundNumber(options.width || 0)
      this.height = MathsUtils.roundNumber(options.height || 0)
      this.extendWidth = MathsUtils.roundNumber(options.extendWidth || 0)
      this.shapeType = options.shapeType || EditorTypes.LibraryItemShapeTypes.custom
      this.cutoutMeasureType = options.cutoutMeasureType || CutoutMeasurementTypes.center
      this.isFlipped = !!options.isFlipped
      this.polished = !!options.polished

      // Setup callbacks next tick so they don't get called when resetting values
      this.callbackContext = options.context
      this.updateCallback = options.updateCallback
      this.applyCallback = options.applyCallback
      this.cancelCallback = options.cancelCallback
    },
    clearOptions() {
      this.callbackContext = null
      this.updateCallback = null
      this.applyCallback = null
      this.cancelCallback = null
    },
    displayToolPopover(containerEl, editor, displayOptions) {
      this.$refs.popover.displayToolPopover(containerEl, editor, displayOptions)
    },
    viewportUpdated(containerEl, editor) {
    },
    hide() {
      if(this.cancelCallback) {
        // Reset cancel CB so it only is called once when hiding popovers
        let cancelCB = this.cancelCallback
        this.cancelCallback = null

        cancelCB(this.callbackContext, false)
      }

      this.clearOptions()
      this.$refs.popover.hide()
    },
    cancelEditing() {
      if(this.cancelCallback)
        this.cancelCallback(this.callbackContext, true)

      this.clearOptions()
      this.hide()
    },
    onShortcutKey(code) {
      switch(code) {
        case 'Escape':
          this.cancelEditing()
          break
      }
    },
    _updateTool() {
      this.updateTool({ apply: false })
    },
    addCutout(eventInfo) {
      this.updateTool({ apply: true, actionTag: eventInfo })
    },
    updateTool(options = {}) {
      try {
        this.radius = MathsUtils.roundNumber(this.parseToolNumber(this.diameter) / 2.0, 1)

        if(options.apply) {
          if(this.applyCallback) {
            this.applyCallback(this.callbackContext, {
              cornerRadius: this.parseToolNumber(this.cornerRadius),
              radius: this.parseToolNumber(this.radius),
              inset: this.parseToolNumber(this.inset),
              cornerOffset: this.parseToolNumber(this.cornerOffset),
              width: this.parseToolNumber(this.width),
              height: this.parseToolNumber(this.height),
              extendWidth: this.parseToolNumber(this.extendWidth),
              cutoutMeasureType: this.cutoutMeasureType,
              actionTag: options.actionTag,
              isFlipped: this.isFlipped,
              polished: this.polished
            })
            this.hide()
          }
        } else {
          if(this.updateCallback) {
            this.updateCallback(this.callbackContext, {
              cornerRadius: this.parseToolNumber(this.cornerRadius),
              radius: this.parseToolNumber(this.radius),
              inset: this.parseToolNumber(this.inset),
              cornerOffset: this.parseToolNumber(this.cornerOffset),
              width: this.parseToolNumber(this.width),
              height: this.parseToolNumber(this.height),
              extendWidth: this.parseToolNumber(this.extendWidth),
              cutoutMeasureType: this.cutoutMeasureType,
              isFlipped: this.isFlipped,
              polished: this.polished
            })
          }
        }

      } catch(err) {
        console.error(err)
        //this.errorText = `${err.message}`
      }

    }
  }
}
</script>